import { useEffect, useState } from 'react'
import { Download, Trash2 } from 'lucide-react'
import { materialsApi } from '@/api/materials'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { Material } from '@/types/material'
import { MaterialThumbnail } from './MaterialThumbnail'

interface MaterialTableProps {
	materials: Material[]
	canDelete: boolean
	onDownload: (id: number) => void
	onDelete: (id: number) => void
}

export function MaterialTable({ materials, canDelete, onDownload, onDelete }: MaterialTableProps) {
	const [preview, setPreview] = useState<{ material: Material; url: string } | null>(null)

	useEffect(() => {
		return () => {
			if (preview?.url) URL.revokeObjectURL?.(preview.url)
		}
	}, [preview])

	const openPreview = async (item: Material) => {
		if (!item.mime.startsWith('image/')) return
		const url = await materialsApi.preview(item.id)
		setPreview((current) => {
			if (current?.url) URL.revokeObjectURL?.(current.url)
			return { material: item, url }
		})
	}

	return (
		<>
			<div className="overflow-x-auto rounded-md border">
				<Table className="min-w-[860px]">
					<TableHeader>
						<TableRow>
							<TableHead className="w-20">类型</TableHead>
							<TableHead>文件名</TableHead>
							<TableHead className="w-28">格式</TableHead>
							<TableHead className="w-28">大小</TableHead>
							<TableHead className="w-44">上传时间</TableHead>
							<TableHead className="w-40 text-right">操作</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{materials.length === 0 ? (
							<TableRow>
								<TableCell colSpan={6} className="h-60 text-center text-muted-foreground">
									暂无素材
								</TableCell>
							</TableRow>
						) : (
							materials.map((item) => (
								<TableRow key={item.id}>
									<TableCell>
										<button
											type="button"
											aria-label={`预览 ${item.original_name}`}
											disabled={!item.mime.startsWith('image/')}
											onClick={() => void openPreview(item)}
											className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-md bg-muted disabled:cursor-default"
										>
											<MaterialThumbnail material={item} className="h-10 w-10 rounded-md object-cover" />
										</button>
									</TableCell>
									<TableCell className="font-medium">
										<span className="block max-w-[320px] truncate" title={item.original_name}>
											{item.original_name}
										</span>
									</TableCell>
									<TableCell className="uppercase text-muted-foreground">{item.ext || '-'}</TableCell>
									<TableCell>{formatBytes(item.size)}</TableCell>
									<TableCell>{formatDateTime(item.uploaded_at || item.created_at)}</TableCell>
									<TableCell className="text-right">
										<div className="inline-flex gap-2">
											<Button type="button" size="sm" variant="outline" onClick={() => onDownload(item.id)}>
												<Download className="mr-1.5 h-3.5 w-3.5" />
												下载
											</Button>
											{canDelete ? (
												<Button type="button" size="sm" variant="destructive" onClick={() => onDelete(item.id)}>
													<Trash2 className="mr-1.5 h-3.5 w-3.5" />
													删除
												</Button>
											) : null}
										</div>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
			<Dialog open={preview !== null} onOpenChange={(open) => !open && setPreview(null)}>
				<DialogContent className="max-h-[92vh] w-[calc(100vw-2rem)] max-w-5xl overflow-hidden p-4">
					<DialogTitle className="truncate pr-8 text-base">{preview?.material.original_name}</DialogTitle>
					<DialogDescription className="sr-only">查看素材原图</DialogDescription>
					{preview ? (
						<div className="flex min-h-0 items-center justify-center overflow-auto rounded-md bg-muted/40">
							<img src={preview.url} alt={preview.material.original_name} className="max-h-[78vh] max-w-full object-contain" />
						</div>
					) : null}
				</DialogContent>
			</Dialog>
		</>
	)
}

function formatBytes(size: number) {
	if (size < 1024) return `${size} B`
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
	return `${(size / 1024 / 1024).toFixed(1)} MB`
}

function formatDateTime(value: string) {
	if (!value) return '-'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return value
	return date.toLocaleString('zh-CN', { hour12: false })
}
